import { safeSanitize } from '../utils/safe_sanitize';

// Clausebook's index already sanitizes and inlines the markup it stores. This
// second boundary uses the same structural vocabulary as handlePasteInline so
// the event cannot carry executable markup while preserving the formatting the
// indexed clause was curated to retain.
const CLAUSEBOOK_SANITIZE_OPTIONS = {
  allowedTags: [
    'b',
    'i',
    'u',
    'p',
    'span',
    'br',
    'input',
    'font',
    'sup',
    'sub',
  ],
  allowedAttributes: {
    '*': ['style'],
    input: ['type', 'value'],
    p: ['data-*', 'class'],
  },
  allowedClasses: {
    p: ['new-paragraph'],
  },
  selfClosing: ['br'],
};

// The generated block is always built carrying the plain text of the clause,
// exactly as an adapted or drafted block is. A lift additionally marks that
// span, and the span is removed only once every precondition for the HTML
// insertion has been checked. Blanking the block up front and hoping the
// dispatch lands is what would put an empty paragraph in the lease when it
// does not -- and an empty inserted paragraph is indistinguishable, to the
// reviewer, from a proposal the model chose to leave blank.
export const LIFT_PLACEHOLDER_ATTRIBUTE = 'data-clausebook-lift';
const LIFT_PLACEHOLDER_SELECTOR = `[${LIFT_PLACEHOLDER_ATTRIBUTE}]`;

// The event the Clausebook iframe already inserts through
// (clipboard_manager.js:301 -> insertClauseHandler -> handlePasteInline). That
// handler owns the sanitizer, the editor bookkeeping and the save; nothing
// here writes markup into the document itself.
const INSERT_EVENT = 'lp:clausebook:insert';

// HTML is insertion-only transport state. Keeping it outside the proposal
// object prevents clause text from entering the closed model schema, generated
// annotation provenance, or audit serialization.
const liftedClauseHtml = new WeakMap();

export function sanitizeClausebookHtml(html) {
  if (typeof html !== 'string') return '';
  return safeSanitize(html, CLAUSEBOOK_SANITIZE_OPTIONS).trim();
}

export function rememberLiftedClauseHtml(proposal, html) {
  if (
    !proposal ||
    typeof proposal !== 'object' ||
    proposal.contentProvenance !== 'lifted'
  ) {
    return proposal;
  }

  const sanitized = sanitizeClausebookHtml(html);
  if (sanitized) liftedClauseHtml.set(proposal, sanitized);
  return proposal;
}

export function hasLiftedClauseHtml(proposal) {
  return Boolean(
    proposal &&
      proposal.contentProvenance === 'lifted' &&
      liftedClauseHtml.has(proposal),
  );
}

function jquery() {
  return (typeof window !== 'undefined' && (window.jQuery || window.$)) || null;
}

// contentEditableLink registers insertClauseHandler from inside
// `$(document).ready(...)`. On jQuery 1.12 that fires synchronously once the
// document is ready, so a marker compiled by $compile is already listening --
// but only if the free-text template actually rendered a contenteditable. In
// admin mode that span sits behind an ng-if and does not exist until a digest,
// and a trigger with nothing listening is silent. Ask before destroying the
// text the block would otherwise show.
function hasInsertHandler(element) {
  const $ = jquery();
  if (!$ || typeof $._data !== 'function') return false;

  const events = $._data(element, 'events');
  const handlers = events && events[INSERT_EVENT];
  return Boolean(handlers && handlers.length);
}

function triggerClausebookInsert(element, eventName, payload) {
  const $ = jquery();
  if (!$) return false;
  $(element).trigger(eventName, payload);
  return true;
}

// The caret goes where the plain-text placeholder was, not at the end of the
// contenteditable: the template's `.EOCE` marker terminates the editable
// region, and handlePasteInline inserts at the caret with range.insertNode.
function placeCaretAtPlaceholder(contentEditable, placeholder) {
  const ownerDocument = contentEditable.ownerDocument || document;
  const selection = ownerDocument.getSelection();
  if (!selection) return false;

  const range = ownerDocument.createRange();
  range.setStartBefore(placeholder);
  range.collapse(true);
  placeholder.remove();

  contentEditable.focus();
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}

/**
 * Replace a generated block's plain-text placeholder with the lifted clause's
 * HTML, through the editor's existing lp:clausebook:insert contract.
 *
 * Returns false -- leaving the block exactly as it was built, showing the
 * clause as text -- whenever the insertion cannot be shown to land. A lift
 * that degrades to the text it was already carrying is the same outcome an
 * adapted proposal has; a lift that degrades to nothing is a blank paragraph
 * in the lease body.
 */
export function insertLiftedClause(
  contentEditable,
  proposal,
  { trigger = triggerClausebookInsert, canInsert = hasInsertHandler } = {},
) {
  if (!contentEditable || !hasLiftedClauseHtml(proposal)) return false;

  const placeholder = contentEditable.querySelector(LIFT_PLACEHOLDER_SELECTOR);
  if (!placeholder) return false;
  if (!canInsert(contentEditable)) return false;
  if (!placeCaretAtPlaceholder(contentEditable, placeholder)) return false;

  const dispatched = trigger(contentEditable, INSERT_EVENT, {
    html: liftedClauseHtml.get(proposal),
  });
  return dispatched !== false;
}
